import React from 'react';
import {Dropdown, DropdownItem, InfoPopover} from "../../components";
import {FinancialModelSelection} from "../models/FinancialModelSelection";
import {ReferenceDataType} from "../../models/referenceData/ReferenceDataType";
import {useAppSelector} from "../../store/hooks";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";

type FinancialModelSelectionProps = {
    financialModel: FinancialModelSelection,
    updateFinancialModel: (newModel: FinancialModelSelection) => void,
    referenceData: ReferenceDataType,
    disabled?: boolean,
    disableAlignmentStrategy?: boolean
}

const NewClientFinancialModel: React.FC<FinancialModelSelectionProps> = ({
                                                                             financialModel,
                                                                             updateFinancialModel,
                                                                             referenceData,
                                                                             disabled = false,
                                                                             disableAlignmentStrategy = false
                                                                         }) => {

    const releaseToggles = useAppSelector(selectReleaseToggles);

    return (
        <>
            <div className="h4 sections modelSelectionLabel">Model Selection</div>
            <hr className="section"/>
            <div className="h4 parent">
                <label className="h5 label accreditedStatusLabel"
                       id="accreditedStatusInput-label"
                       data-testid="accreditedStatusLabel"
                >
                    Accredited Status
                </label>
                <InfoPopover
                    content={
                        <div className="popover-content"><b>Accredited Status</b> should reflect whether the proposed
                            fulfillment includes Private Equity & Hedge.</div>
                    }
                    direction="bottom"
                    label="accredited-status"
                    width={"288px"}
                />
                <Dropdown
                    className="accreditedStatus paddingbottom-12"
                    aria-label="accreditedStatusInput"
                    aria-labelledby="accreditedStatusInput-label"
                    alignRight={false}
                    buttonIcon="right"
                    buttonKind="secondary"
                    defaultPageSize={15}
                    disabled={disabled}
                    dropdownKind="select"
                    dynamicDropAlign={false}
                    dynamicDropDirection={false}
                    data-testid="accreditedStatusInput"
                    id="accreditedStatusInput"
                    iconNameClose="arrow_drop_up"
                    iconNameOpen="arrow_drop_down"
                    labelWidth="300px"
                    nativeOnMobile={false}
                    panelHeight="auto"
                    panelWidth="300px"
                    defaultText="Select..."
                    onChange={(e: any) => {
                        updateFinancialModel({
                            ...financialModel,
                            accreditedStatus: e.itemText
                        });
                    }}
                    buttonSize="medium"
                    virtualScroll={false}
                    value={financialModel.accreditedStatus}
                >
                    {referenceData.accreditedStatus.items.map(status => (
                        <DropdownItem key={status} itemText={status} value={status}/>
                    ))}
                </Dropdown>
            </div>
            <div className="h4 parent">
                <label className="h5 label allocationTypeLabel"
                       id="allocationTypeInput-label"
                       data-testid="allocationTypeLabel"
                >
                    Allocation Type
                </label>
                <InfoPopover
                    content={
                        <div className="popover-content"><b>Allocation Type</b> should reflect fulfillment for risk assets. The risk asset model will follow the Maximum Growth Investment Objective for the selected type - Tactical or Strategic.</div>
                    }
                    direction="bottom"
                    label="allocation-type"
                    width={"288px"}
                />
                <Dropdown
                    className="allocationTypeInput"
                    aria-label="allocationTypeInput"
                    data-testid="allocationTypeInput"
                    aria-labelledby="allocationTypeInput-label"
                    alignRight={false}
                    buttonIcon="right"
                    buttonKind="secondary"
                    defaultPageSize={15}
                    dropdownKind="select"
                    disabled={disabled}
                    dynamicDropAlign={false}
                    dynamicDropDirection={false}
                    id="allocationTypeInput"
                    iconNameClose="arrow_drop_up"
                    iconNameOpen="arrow_drop_down"
                    labelWidth="300px"
                    nativeOnMobile={false}
                    panelHeight="auto"
                    panelWidth="300px"
                    defaultText="Select..."
                    onChange={(e: any) => {
                        updateFinancialModel({
                            ...financialModel,
                            allocationType: e.itemText
                        });
                    }}
                    buttonSize="medium"
                    virtualScroll={false}
                    value={financialModel.allocationType}
                >
                    {referenceData.allocationType.items.map(status => (
                        <DropdownItem key={status} itemText={status} value={status}/>
                    ))}
                </Dropdown>
            </div>
            {releaseToggles?.enableEnhancedLifestyleFunding && <div className="h4 parent">
                <label className="h5 label alignmentStrategyLabel"
                       id="alignmentStrategyInput-label"
                       htmlFor="alignmentStrategyInput"
                       data-testid="alignmentStrategyTypeLabel"
                >
                    Asset/Goal Alignment
                </label>
                <InfoPopover
                    content={
                        <div className="popover-content">
                            <b>Asset/Goal Alignment</b> reflects the order in which assets fund goals.
                            <br/><br/>
                            <b>Accumulation</b> funds goals in the following order: Future Inflows, Investable Portfolio, Savings, Non-Investable Assets.
                        </div>
                    }
                    direction="bottom"
                    label="alignment-strategy"
                    width={"288px"}
                />
                <Dropdown
                    className="alignmentStrategyInput"
                    aria-label="alignmentStrategyInput"
                    data-testid="alignmentStrategyInput"
                    aria-labelledby="alignmentStrategyInput-label"
                    alignRight={false}
                    buttonIcon="right"
                    buttonKind="secondary"
                    defaultPageSize={15}
                    dropdownKind="select"
                    disabled={disabled || disableAlignmentStrategy}
                    dynamicDropAlign={false}
                    dynamicDropDirection={false}
                    id="alignmentStrategyInput"
                    iconNameClose="arrow_drop_up"
                    iconNameOpen="arrow_drop_down"
                    labelWidth="300px"
                    nativeOnMobile={false}
                    panelHeight="auto"
                    panelWidth="300px"
                    defaultText="Select..."
                    onChange={(e: any) => {
                        updateFinancialModel({
                            ...financialModel,
                            alignmentStrategy: e.itemText
                        });
                    }}
                    buttonSize="medium"
                    virtualScroll={false}
                    value={financialModel.alignmentStrategy}
                >
                    <DropdownItem key="Accumulation" itemText="Accumulation" value="Accumulation"/>
                </Dropdown>


            </div>}
        </>
    );
};

export default NewClientFinancialModel;
